import { Outlet, Navigate } from "react-router-dom";
import { Shield, Lock } from "lucide-react";
import { useAuthStore } from "@/lib/auth";

export function AuthLayout() {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-red-500/10 flex items-center justify-center animate-pulse">
            <Shield className="h-6 w-6 text-red-500" />
          </div>
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-red-500/20 border-t-red-500" />
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-red-950 via-red-900 to-red-950">
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255, 59, 48, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 59, 48, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
          }}
        />

        {/* Glow effects */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-red-600/15 rounded-full blur-3xl" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-white/10 backdrop-blur flex items-center justify-center border border-white/20">
              <Shield className="h-5 w-5" />
            </div>
            <div>
              <span className="font-bold text-lg">ASM Toolkit</span>
              <span className="text-red-300 text-sm block -mt-0.5">Admin Portal</span>
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <Lock className="h-5 w-5 text-red-300" />
              <span className="text-red-200 text-sm">Secure Administrative Access</span>
            </div>
            <h1 className="text-4xl font-bold leading-tight">
              System<br />
              <span className="text-red-300">Administration</span>
            </h1>
            <p className="text-red-200/80 max-w-md">
              Manage organizations, users, tiers, and monitor reconnaissance activities across the platform.
            </p>
          </div>

          <div className="text-sm text-red-300/60">
            © {new Date().getFullYear()} Sertua
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex justify-center mb-8 lg:hidden">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg shadow-red-500/20">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <span className="font-bold text-lg text-foreground">Admin Portal</span>
                <span className="text-muted-foreground text-xs block">Sertua</span>
              </div>
            </div>
          </div>

          <Outlet />
        </div>
      </div>
    </div>
  );
}
